import { Pencil, GripVertical } from "lucide-react";
import DOMPurify from "dompurify";
import { StatusTag } from "../../ui/StatusTag";
import { formatText } from "../../../_utils/textFormatter";
import "./_catalog-card.scss";

// Presentation-only shorthand for a real billingCycle/pricingCycle value
// (Daily/Weekly/Monthly/Quarterly/Half-yearly/Yearly, per _utils/pricing's
// CYCLE_TO_DAYS) - reformats an existing field, doesn't invent one.
const CYCLE_ABBREV = {
  Daily: "day",
  Weekly: "wk",
  Monthly: "mo",
  Quarterly: "qtr",
  "Half-yearly": "6mo",
  Yearly: "yr",
};

// Single source of truth for status -> visuals - the dot color is derived
// from the same `type` StatusTag renders (one status->type mapping, not two
// separately-keyed tables that could drift out of sync).
const STATUS_CONFIG = {
  active: { type: "active", label: "Live" },
  draft: { type: "draft", label: "Draft" },
  archive: { type: "archive", label: "Archived" },
  archived: { type: "archive", label: "Archived" },
  inactive: { type: "warning", label: "In active" },
};

const DOT_COLOR_BY_TYPE = {
  active: "var(--catalog-card-dot-active)",
  draft: "var(--catalog-card-dot-draft)",
  archive: "var(--catalog-card-dot-archive)",
  warning: "var(--catalog-card-dot-draft)",
};

const getPriceDisplay = (kind, data) => {
  const { pricingModel, amount, price, pricingTiers, pricingCycle, unitName, market } = data || {};
  const sign = market?.currency?.[0]?.sign || "$";
  const cycleAbbrev = CYCLE_ABBREV[pricingCycle] || null;
  const mainAmount = kind === "Product" ? price : amount;

  if (["Volume", "Tiered"].includes(pricingModel)) {
    return {
      main: "Custom",
      qualifier: pricingTiers?.length
        ? `${pricingTiers.length} tier${pricingTiers.length > 1 ? "s" : ""}`
        : "tiered pricing",
    };
  }

  if (pricingModel === "Per Unit") {
    return {
      main: mainAmount ? `${sign}${mainAmount}` : null,
      unit: `per ${unitName || "unit"}${cycleAbbrev ? ` / ${cycleAbbrev}` : ""}`,
    };
  }

  return {
    main: mainAmount != null && mainAmount !== "" ? `${sign}${mainAmount}` : null,
    unit: cycleAbbrev ? `/ ${cycleAbbrev}` : null,
  };
};

/**
 * Compact metrics-style card for the Plan/Product gallery views, matching
 * the new gallery design. Distinct from the shared PlanCard (checkout,
 * live-preview) - this reads only fields real API responses already return
 * (name, price, description, status, market, billing cycle, bundle/fee
 * type) and intentionally has no Accounts/MRR-contribution/attach-rate rows
 * since no endpoint computes those yet.
 */
const CatalogCard = ({ kind, data, onEdit, isDrag = false }) => {
  const { description, status, market } = data || {};
  const name = kind === "Product" ? data?.productName : data?.planName;
  // No status at all (legacy data predating status defaulting on create)
  // reads as Draft, same as the Plans table's Status column.
  const statusKey = status?.toLowerCase() || "draft";
  const statusConfig = STATUS_CONFIG[statusKey] || STATUS_CONFIG.draft;
  const dotColor = DOT_COLOR_BY_TYPE[statusConfig?.type] || "var(--catalog-card-dot-draft)";

  const priceDisplay = getPriceDisplay(kind, data);

  const metrics =
    kind === "Product"
      ? [
          { label: "Fee type", value: formatText(data?.feeType) || "—" },
          { label: "Billing cycle", value: formatText(data?.billingCycle) || "—" },
          { label: "Add-on", value: data?.isAddOn ? "Yes" : "No" },
        ]
      : [
          { label: "Market", value: formatText(market?.marketName) || "—" },
          { label: "Billing cycle", value: formatText(data?.billingCycle) || "—" },
          { label: "Bundle", value: formatText(data?.bundle?.name) || "Unbundled" },
        ];

  return (
    <div className="catalog-card">
      <div className="catalog-card__top">
        <div className="catalog-card__identity">
          {isDrag && <GripVertical className="catalog-card__drag-icon" size={14} />}
          <span className="catalog-card__dot" style={{ backgroundColor: dotColor }} />
          <h3 className="catalog-card__name">{name || `${kind} name`}</h3>
        </div>
        <div className="catalog-card__top-right">
          {statusConfig && <StatusTag type={statusConfig.type} label={statusConfig.label} showIcon={false} />}
          {onEdit && (
            <button type="button" onClick={() => onEdit(data)} className="catalog-card__edit-icon" aria-label={`Edit ${kind.toLowerCase()}`}>
              <Pencil size={10} />
            </button>
          )}
        </div>
      </div>

      <div className="catalog-card__price">
        {priceDisplay.main ? <span className="catalog-card__price-main">{priceDisplay.main}</span> : <span className="catalog-card__price-main catalog-card__price-main--muted">Enter amount</span>}
        {priceDisplay.unit && <span className="catalog-card__price-unit">{priceDisplay.unit}</span>}
        {priceDisplay.qualifier && <span className="catalog-card__price-qualifier">{priceDisplay.qualifier}</span>}
      </div>

      <div
        className="catalog-card__desc"
        // `description` is free-text, not authored HTML - unsanitized, this
        // executed arbitrary JS via e.g. `<img src=x onerror=...>` for any
        // org member viewing this card (shared by Products/Plans/Bundles),
        // confirmed live during the Products CRUD test.
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(
            description && description !== "<p><br></p>" ? description : "Description"
          ),
        }}
      />

      <div className="catalog-card__divider" />

      <div className="catalog-card__metrics">
        {metrics.map((metric) => (
          <div className="catalog-card__metric-row" key={metric.label}>
            <span className="catalog-card__metric-label">{metric.label}</span>
            <span className="catalog-card__metric-value">{metric.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CatalogCard;
